import type { ReactElement } from "react";
import { ClusterOverview } from "@/features/overview/ClusterOverview";
import { NodesView } from "@/features/nodes/NodesView";
import { NodeDetail } from "@/features/nodes/NodeDetail";
import { NamespaceList } from "@/features/drill/NamespaceList";
import { NamespaceDetail } from "@/features/drill/NamespaceDetail";
import { WorkloadDetail } from "@/features/drill/WorkloadDetail";
import { PodDetail } from "@/features/drill/PodDetail";
import { TopologyView } from "@/features/topology/TopologyView";
import { ManageView } from "@/features/manage/ManageView";
import { ManualWorkloadManageView } from "@/features/manage/ManualWorkloadManageView";
import { DebugSessions } from "@/features/debug/DebugSessions";
import { ResourcesView } from "@/features/resources/ResourcesView";
import { ResourceMapEntry } from "@/features/resources/ResourceMapEntry";
import { AlertsView } from "@/features/alerts/AlertsView";
import { EventsView } from "@/features/events/EventsView";
import { WorkloadsOverview } from "@/features/workloads/WorkloadsOverview";
import { GitOpsApplicationsView } from "@/features/gitops/GitOpsApplicationsView";
import { DashboardView } from "@/features/dashboards/DashboardView";
import { DashboardBuilderEditor, DashboardBuilderList } from "@/features/dashboard-builder/DashboardBuilder";
import { ExtensionRoute } from "@/app/extensions/ExtensionRoute";
import { routeContributions } from "@/app/extensions/contributions";
import {
  ROUTE_META,
  navRoutes as navMeta,
  navRoutesInGroup as navMetaInGroup,
  type RouteCapabilities,
  type RouteGroup,
  type RouteMeta,
} from "@/app/routeRegistry";

/**
 * 라우트 element 결합 (ADR 0023 Phase 1)
 * --------------------------------------------------------------------------
 * 라우트 **사실**은 `routeRegistry.ts`에 있고, 이 파일은 거기에 화면 element만
 * 붙입니다. 둘로 나눈 이유는 순환 import입니다 — breadcrumb(`components/drill.tsx`)은
 * 화면들이 다시 import하는 모듈이라, 라우트 표가 화면을 import하면 breadcrumb이
 * 같은 표를 읽을 수 없습니다. 사실만 leaf로 내려두면 nav·breadcrumb·팔레트가
 * 모두 같은 배열 하나를 읽습니다.
 *
 * element는 **id로만** 결합합니다. path 문자열을 두 곳에 적으면 한쪽만 고치는
 * 사고가 다시 생깁니다.
 */

export type { RouteGroup, RouteCapability, RouteCapabilities, RouteMeta } from "@/app/routeRegistry";
export { NAV_GROUPS, ROUTE_META, routeAllowed, routeCrumb, routeMetaById } from "@/app/routeRegistry";

/** 레지스트리 항목 + 화면. 라우터가 그대로 `<Route>`로 펼칩니다. */
export interface AppRoute extends RouteMeta {
  element: ReactElement;
}

/** id → 화면. 레지스트리에 있는 id가 여기에 없으면 빌드가 아니라 테스트가 잡습니다. */
const CORE_ELEMENTS: Record<string, ReactElement> = {
  overview: <ClusterOverview />,
  nodes: <NodesView />,
  "node-detail": <NodeDetail />,
  namespaces: <NamespaceList />,
  "namespace-detail": <NamespaceDetail />,
  "workload-detail": <WorkloadDetail />,
  "manage-workload": <ManualWorkloadManageView />,
  "pod-detail": <PodDetail />,
  topology: <TopologyView />,
  events: <EventsView />,
  workloads: <WorkloadsOverview />,
  alerts: <AlertsView />,
  resources: <ResourcesView />,
  gitops: <GitOpsApplicationsView />,
  "gitops-detail": <GitOpsApplicationsView />,
  "resource-map": <ResourceMapEntry />,
  deployments: <ManageView kind="deployments" />,
  secrets: <ManageView kind="secrets" />,
  "debug-sessions": <DebugSessions />,
  dashboard: <DashboardView />,
  "dashboard-builder": <DashboardBuilderList />,
  "dashboard-builder-editor": <DashboardBuilderEditor />,
};

/**
 * 확장이 준 라우트의 element (ADR 0024). 화면마다 손으로 적지 않습니다 — 기여
 * 목록에서 그대로 만들어지고, 각 화면은 확장 호스트가 격리해 지연 로드합니다.
 */
const EXTENSION_ELEMENTS: Record<string, ReactElement> = Object.fromEntries(
  routeContributions().map((route) => [
    route.id,
    <ExtensionRoute key={route.id} extensionId={route.extensionId} routeId={route.id} />,
  ]),
);

const ELEMENTS: Record<string, ReactElement> = { ...CORE_ELEMENTS, ...EXTENSION_ELEMENTS };

export const APP_ROUTES: AppRoute[] = ROUTE_META.map((meta) => ({ ...meta, element: ELEMENTS[meta.id] }));

/** 좌측 nav와 팔레트가 함께 쓰는 목적지 목록. 순서는 레지스트리 순서입니다. */
export function navRoutes(caps: RouteCapabilities): AppRoute[] {
  const allowed = new Set(navMeta(caps).map((r) => r.id));
  return APP_ROUTES.filter((r) => allowed.has(r.id));
}

export function navRoutesInGroup(group: RouteGroup, caps: RouteCapabilities): AppRoute[] {
  const allowed = new Set(navMetaInGroup(group, caps).map((r) => r.id));
  return APP_ROUTES.filter((r) => allowed.has(r.id));
}
